import { z } from 'zod';

/** §SI/§IR/§YT/§FT — KSK Panel'den taşınan modüllerin girdi doğrulaması. */

// ---- Sipariş ID defteri ------------------------------------------------

export const siparisIdSchema = z
  .object({
    seller: z.string().max(60).nullish(),
    buyer: z.string().max(60).nullish(),
    tarih: z.string().max(40).nullish(),
    note: z.string().max(1000).nullish(),
    status: z.enum(['', 'BEKLEMEDE', 'IADE_GELDI', 'TAMAMLANDI']).default(''),
  })
  .refine((v) => [v.seller, v.buyer, v.note].some((a) => (a ?? '').trim() !== ''), {
    message: 'Satıcı no, alıcı no ya da nottan en az birini yazın',
    path: ['seller'],
  });
export type SiparisIdDto = z.infer<typeof siparisIdSchema>;

// ---- İhlal radarı ------------------------------------------------------

export const ihlalKutuSchema = z.object({
  email: z.string().email('Geçerli bir e-posta adresi girin'),
  parola: z.string().min(1, 'Parola gerekli').max(200),
  /** Boş bırakılırsa e-posta adresinden tespit ediliyor. */
  imapSunucu: z.string().max(200).optional(),
});
export type IhlalKutuDto = z.infer<typeof ihlalKutuSchema>;

export const ihlalTaramaSchema = z.object({
  /** Boşsa tüm kutular taranıyor. */
  kutuId: z.string().nullish(),
  /** Boş = son 90 gün; 0 = tüm gelen kutusu. En fazla iki yıl geriye bakılıyor. */
  gunSayisi: z
    .number()
    .int()
    .min(0)
    .max(730, 'En fazla 730 gün geriye bakılabilir; tüm kutu için 0 yazın')
    .nullish(),
});
export type IhlalTaramaDto = z.infer<typeof ihlalTaramaSchema>;

/*
 * 31 Eki 2026: üst sınır 500'dü — "türe göre seç" ile binlerce ASIN seçilebiliyor (SA'da tek türde
 * 11.191). 50.000 = yasaklı ürün listesinin tek seferde işlediği en çok satır (`MAX_ASINS_PER_DELETE`).
 */
export const ihlalYasaklaSchema = z.object({
  asinler: z
    .array(z.string().min(1).max(20))
    .min(1, 'En az bir ürün seçin')
    .max(50_000, 'Tek seferde en fazla 50.000 ürün kısıtlanabilir'),
});
export type IhlalYasaklaDto = z.infer<typeof ihlalYasaklaSchema>;

/** Excel'e aktarılacak ASIN'ler — ekranda görünenler ya da seçilenler, ekrandaki sırayla. */
export const ihlalDisaAktarSchema = z.object({
  asinler: z
    .array(z.string().min(1).max(20))
    .min(1, 'İndirilecek ürün yok')
    .max(100_000, 'Tek seferde en fazla 100.000 ürün indirilebilir'),
});
export type IhlalDisaAktarDto = z.infer<typeof ihlalDisaAktarSchema>;

// ---- Yorum tespiti -----------------------------------------------------

export const yorumAnalizSchema = z.object({
  satirlar: z
    .array(z.object({ orderId: z.string(), teslimTarihi: z.string() }))
    .max(5000, 'Tek seferde en fazla 5.000 satır'),
});
export type YorumAnalizDto = z.infer<typeof yorumAnalizSchema>;

export const yorumIsaretSchema = z.object({
  orderIds: z
    .array(z.string().min(1))
    .min(1, 'En az bir sipariş seçin')
    .max(5000, 'Tek seferde en fazla 5.000 sipariş işaretlenebilir'),
});
export type YorumIsaretDto = z.infer<typeof yorumIsaretSchema>;

// ---- Fatura oluşturucu -------------------------------------------------

export const faturaLogoSchema = z.object({
  name: z.string().min(1, 'Logoya bir isim verin').max(60),
  dataUrl: z.string().min(1).startsWith('data:image/', 'Yalnızca görsel dosyası yüklenebilir'),
});
export type FaturaLogoDto = z.infer<typeof faturaLogoSchema>;

export const faturaSchema = z.object({
  supplier: z.string().min(1, 'Tedarikçi bilgisi gerekli').max(500),
  buyer: z.string().min(1, 'Alıcı bilgisi gerekli').max(500),
  invNo: z.string().min(1, 'Fatura numarası gerekli').max(60),
  invDate: z.string().min(1, 'Fatura tarihi gerekli').max(40),
  currency: z.string().min(1).max(20),
  payment: z.string().max(120).default(''),
  prodDesc: z.string().min(1, 'Ürün açıklaması gerekli').max(2000),
  qty: z.number().int().min(1).max(100_000),
  price: z.number().min(0).max(1_000_000),
  taxRate: z.number().min(0).max(100),
  shipping: z.number().min(0).max(1_000_000),
  terms: z.string().max(2000).default(''),
  bankDetails: z.string().max(2000).default(''),
  logoId: z.string().nullish(),
  logosuz: z.boolean().default(false),
});
export type FaturaDto = z.infer<typeof faturaSchema>;
